import { Box, CircularProgress } from '@mui/material';
import React, { useCallback, useMemo, useState } from 'react';

import { INVENTORY_ITEMS_PER_PAGE_WHEN_SCROLLED_INFINITELY } from '../../../constants/inventories';
import useDebouncedCallback from '../../../hooks/useDebouncedCallback';
import useElementScrollTracker from '../../../hooks/useElementScrollTracker';
import useInfiniteScroll from '../../../hooks/useInfiniteScroll';
import { useItemsInfiniteScroll } from '../../../service/InventoryService';
import { IInventoryItem } from '../../../types/inventories';
import { isBrokenMSKU, isItemDigital, isItemOutOfStock } from '../../../utils/inventories';
import { InventoryItem } from '../../index';
import { FormControls } from '../../SummaryComponentMUI/components/common';
import { AddSection, SearchInput } from './index';

import styles from '../../../containers/AddSendItemsSidebar/AddSendItemsContainer/AddSendItemsContainer.module.scss';

interface IProps {
  onClose: () => void;
  items: IInventoryItem[];
  onSubmit: (items: IInventoryItem[]) => void;
}

export const AddInventoryItems: React.FC<IProps> = ({ onClose, items, onSubmit }) => {
  const [input, setInput] = useState('');
  const [query, setQuery] = useState(input);
  const [selectedItems, setSelectedItems] = useState(items);

  const containerRef = React.useRef(null);

  const {
    data: inventoryData,
    isFetching,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    isFetchedAfterMount,
  } = useItemsInfiniteScroll({
    page_size: INVENTORY_ITEMS_PER_PAGE_WHEN_SCROLLED_INFINITELY,
    search_query: query,
  });

  const inventoryList = useMemo<IInventoryItem[]>(
    () => (inventoryData?.pages.flat() as IInventoryItem[]) || [],
    [inventoryData],
  );

  const handleScroll = useInfiniteScroll({
    hasNextPage,
    isFetchingNextPage,
    fetchNextPage,
    threshold: 0.75,
  });

  // Since next page fetch is triggered by the scroll event we need this hook
  // to track whether it's enough items to fulfill the height of the dropdown and cause scroll
  // if there's still a page to fetch and there's no scroll on the UI, it will fetch the next page
  useElementScrollTracker({
    trigger: isFetchedAfterMount && !!inventoryList.length && hasNextPage && !isFetching,
    ref: containerRef,
    callback: fetchNextPage,
  });

  const debouncedSetQuery = useDebouncedCallback((v: string) => {
    setQuery(v);
  }, 500);

  const onChange = useCallback(
    (v: string) => {
      setInput(v);
      debouncedSetQuery(v);
    },
    [debouncedSetQuery],
  );

  const checkIsItemSelected = useCallback(
    (itemId: string) => selectedItems?.some((item) => item.item_id === itemId),
    [selectedItems],
  );

  const handleAddItem = useCallback((item: IInventoryItem) => {
    setSelectedItems((prev) => [...prev, item]);
  }, []);

  const handleRemoveItem = useCallback((itemId: string) => {
    setSelectedItems((prev) => prev?.filter((item) => item.item_id !== itemId));
  }, []);

  const handleCancel = useCallback(() => {
    setSelectedItems(items);
    onClose();
  }, [onClose, items]);

  const handleSubmit = useCallback(() => {
    if (selectedItems) {
      onSubmit(selectedItems as IInventoryItem[]);
    }
    onClose();
  }, [onClose, onSubmit, selectedItems]);

  const allowedFulfillmentCenter = React.useMemo(() => {
    if (selectedItems?.length) {
      return selectedItems[0].fulfillment_center_id;
    }
  }, [selectedItems]);

  const isFirstItemsFetching = useMemo(() => {
    return isFetching && !isFetchingNextPage && inventoryList.length === 0;
  }, [isFetching, isFetchingNextPage, inventoryList]);

  const getIsItemDisabled = React.useCallback(
    (item: IInventoryItem) => {
      const isDisabledByFC = allowedFulfillmentCenter
        ? !(allowedFulfillmentCenter === item.fulfillment_center_id)
        : false;

      return isItemDigital(item) || isBrokenMSKU(item) || isItemOutOfStock(item) || isDisabledByFC;
    },
    [allowedFulfillmentCenter],
  );

  const renderInventoryItems = () => {
    return inventoryList?.map((item: IInventoryItem) => {
      const {
        item_id: itemId,
        name,
        image_url: icon,
        price,
        count,
        type,
        sku_options: skuOptions,
        item_customization_allowed: isCustomizable,
        fulfillment_center_id,
        preorder_allowed: isPreorderAllowed,
      } = item;

      const isDisabled = getIsItemDisabled(item);

      const handleItemClick = () => (checkIsItemSelected(itemId) ? handleRemoveItem(itemId) : handleAddItem(item));

      return (
        <Box sx={{ height: 250, width: 180 }} key={itemId}>
          <InventoryItem
            key={itemId}
            itemId={itemId}
            title={name}
            icon={icon}
            price={price}
            count={count}
            type={type}
            skuOptions={skuOptions}
            fulfillment_center_id={fulfillment_center_id}
            isSelected={checkIsItemSelected(itemId)}
            isDisabled={isDisabled}
            isCustomizable={isCustomizable}
            className={styles.item}
            iconClassName={styles.iconInner}
            isPreorder={isPreorderAllowed}
            onSelect={handleItemClick}
          />
        </Box>
      );
    });
  };

  const isLoading = useMemo(() => {
    return isFirstItemsFetching || isFetchingNextPage;
  }, [isFirstItemsFetching, isFetchingNextPage]);

  const renderSpinner = () => {
    return (
      <Box
        sx={{
          gridColumn: '1 / -1',
          display: 'flex',
          height: '100px',
          justifyContent: 'center',
          alignItems: 'center',
        }}
      >
        <CircularProgress size={28} />
      </Box>
    );
  };

  return (
    <AddSection title="Add items" onClose={handleCancel}>
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          height: '100%',
        }}
      >
        <SearchInput value={input} onChange={(v) => onChange(v as string)} placeholderText="items" />
        <Box
          sx={{
            my: 2,
            flex: '1 1 auto',
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 195px))',
            gridAutoRows: '260px',
            gap: 1,
            overflowY: 'auto',
            minHeight: 'calc(100vh - 280px)',
            maxHeight: 'calc(100vh - 280px)',
          }}
          onScroll={handleScroll}
          ref={containerRef}
        >
          {renderInventoryItems()}
          {isLoading && renderSpinner()}
        </Box>
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'flex-end',
            mr: '-3px',
            pt: '9px',
          }}
        >
          <FormControls
            onCancel={handleCancel}
            onSubmit={handleSubmit}
            isDisabled={!selectedItems?.length}
            customText="Continue"
          />
        </Box>
      </Box>
    </AddSection>
  );
};
